import { cn } from "@/lib/utils"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap font-medium ring-offset-background transition-colors focus-visible_outline-none focus-visible_ring-2 focus-visible_ring-ring focus-visible_ring-offset-2 disabled_pointer-events-none disabled_opacity-50",
  {
    variants: {
      variant: {
        default:
          "bg-primary font-sans font-[800] text-white hover_bg-primary/90 uppercase",
        destructive:
          "bg-destructive font-sans text-destructive-foreground hover_bg-destructive/90",
        outline:
          "border border-primary bg-white hover_bg-primary hover_text-white",
        secondary:
          "bg-secondary text-secondary-foreground hover_bg-secondary/80",
        ghost: "hover_bg-accent hover_text-accent-foreground",
        link: "text-primary underline-offset-4 hover_underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 px-3",
        lg: "h-11 px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"

    return (
      <Comp
        className={cn(
          buttonVariants({
            variant,
            size,
            className,
          })
        )}
        ref={ref}
        {...props}
      />
    )
  }
)

Button.displayName = "Button"

export { Button, buttonVariants }
